import React, { useEffect, useRef, useState } from 'react';
import { Input, Button, Popover } from 'antd';
import {
    MdFormatBold, MdFormatItalic, MdFormatUnderlined,
    MdFormatListBulleted, MdFormatListNumbered,
    MdFormatAlignLeft, MdFormatAlignCenter, MdFormatAlignRight, MdFormatAlignJustify,
    MdFormatClear, MdAdd, MdFormatColorText, MdInsertLink,
} from 'react-icons/md';

const TOOLBAR_GROUPS = [
    [
        { cmd: 'bold',      Icon: MdFormatBold,       title: 'Bold' },
        { cmd: 'italic',    Icon: MdFormatItalic,     title: 'Italic' },
        { cmd: 'underline', Icon: MdFormatUnderlined, title: 'Underline' },
    ],
    [
        { cmd: 'insertUnorderedList', Icon: MdFormatListBulleted, title: 'Bullet list' },
        { cmd: 'insertOrderedList',   Icon: MdFormatListNumbered, title: 'Numbered list' },
    ],
    [
        { cmd: 'justifyLeft',   Icon: MdFormatAlignLeft,    title: 'Align left' },
        { cmd: 'justifyCenter', Icon: MdFormatAlignCenter,  title: 'Align center' },
        { cmd: 'justifyRight',  Icon: MdFormatAlignRight,   title: 'Align right' },
        { cmd: 'justifyFull',   Icon: MdFormatAlignJustify, title: 'Justify' },
    ],
    [
        { cmd: 'removeFormat', Icon: MdFormatClear, title: 'Clear formatting' },
    ],
];

const VAR_RE = /{{[^}]+}}/g;

// Stylesheet injected into the iframe head — keeps var styling out of inline style=""
// so execCommand('removeFormat') cannot strip it.
const IFRAME_CSS = `
[data-var="true"] {
  background: #fff7e6;
  color: #d46b08;
  border: 1px dashed #ffc069;
  border-radius: 4px;
  padding: 1px 5px;
  white-space: nowrap;
  cursor: default;
  font-style: normal;
  font-weight: normal;
  text-decoration: none;
}`;

// Walk text nodes and wrap any {{...}} occurrences with a contenteditable=false span
// so the browser treats each variable as an atomic unit — it cannot be partially selected
// or split by execCommand (bold/italic/etc).
function wrapVarNodes(doc) {
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    const targets = [];

    while (walker.nextNode()) {
        const node = walker.currentNode;
        // Skip text already inside one of our var spans
        let el = node.parentElement;
        while (el && el !== doc.body) {
            if (el.dataset?.var === 'true') break;
            el = el.parentElement;
        }
        if (el && el.dataset?.var === 'true') continue;

        VAR_RE.lastIndex = 0;
        if (VAR_RE.test(node.textContent)) targets.push(node);
    }

    targets.forEach((textNode) => {
        const frag = doc.createDocumentFragment();
        let last = 0;
        VAR_RE.lastIndex = 0;
        let m;
        while ((m = VAR_RE.exec(textNode.textContent)) !== null) {
            if (m.index > last) {
                frag.appendChild(doc.createTextNode(textNode.textContent.slice(last, m.index)));
            }
            const span = doc.createElement('span');
            span.contentEditable = 'false';
            span.dataset.var = 'true';
            span.textContent = m[0];
            frag.appendChild(span);
            last = m.index + m[0].length;
        }
        if (last < textNode.textContent.length) {
            frag.appendChild(doc.createTextNode(textNode.textContent.slice(last)));
        }
        textNode.parentNode.replaceChild(frag, textNode);
    });
}

// Strip our wrapper spans — preserve any formatting that was applied inside
function stripVarSpans(html) {
    return html.replace(/<span\b[^>]*\bdata-var="true"[^>]*>([\s\S]*?)<\/span>/g, '$1');
}

const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'LI', 'BLOCKQUOTE', 'TD', 'TH']);
const LINE_HEIGHTS = ['1', '1.2', '1.5', '1.75', '2', '2.5', '3'];
const FONT_SIZES = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36];

const COLORS = [
    // Neutrals
    '#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#ffffff',
    // Reds / Pinks
    '#ff0000', '#cc0000', '#990000', '#660000', '#ff4081', '#f48fb1', '#ff80ab', '#ffcdd2',
    // Oranges / Browns
    '#ff6d00', '#e65100', '#bf360c', '#7b3f00', '#ff9800', '#ffb74d', '#795548', '#a1887f',
    // Yellows / Golds
    '#ffff00', '#ffd600', '#f9a825', '#f57f17', '#ffe082', '#fff9c4', '#ffee58', '#ffca28',
    // Greens
    '#00ff00', '#00c853', '#1b5e20', '#33691e', '#76ff03', '#b9f6ca', '#69f0ae', '#43a047',
    // Cyans / Teals
    '#00ffff', '#00b8d4', '#006064', '#00838f', '#80deea', '#b2ebf2', '#26c6da', '#00acc1',
    // Blues
    '#0000ff', '#1565c0', '#0d47a1', '#003399', '#42a5f5', '#90caf9', '#bbdefb', '#1976d2',
    // Purples / Violets
    '#9c27b0', '#7b1fa2', '#4a148c', '#6a1b9a', '#ce93d8', '#e1bee7', '#8e24aa', '#ab47bc',
];


// Returns all block-level elements touched by the saved range
function getSelectedBlocks(doc, range) {
    const findBlock = (node) => {
        let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
        while (el && el !== doc.body) {
            if (BLOCK_TAGS.has(el.tagName)) return el;
            el = el.parentElement;
        }
        return null;
    };

    const blocks = new Set();
    const startBlock = findBlock(range.startContainer);
    if (startBlock) blocks.add(startBlock);

    if (!range.collapsed) {
        const endBlock = findBlock(range.endContainer);
        if (endBlock) blocks.add(endBlock);
        // pick up any fully-contained block children of the common ancestor
        const ancestor = range.commonAncestorContainer;
        if (ancestor.nodeType === Node.ELEMENT_NODE) {
            for (const child of ancestor.children) {
                if (BLOCK_TAGS.has(child.tagName) && range.intersectsNode(child)) {
                    blocks.add(child);
                }
            }
        }
    }
    return [...blocks];
}

const btnBase = {
    border: '1px solid transparent',
    background: 'transparent',
    borderRadius: 4,
    padding: '3px 5px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#444',
};

const HtmlEditor = ({ value, onChange, onVarInsert }) => {
    const iframeRef = useRef(null);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const [varPopOpen, setVarPopOpen] = useState(false);
    const [varName, setVarName] = useState('');
    const [lineHeight, setLineHeight] = useState('');
    const [fontSize, setFontSize] = useState('14');
    const [colorPopOpen, setColorPopOpen] = useState(false);
    const [activeColor, setActiveColor] = useState('#000000');
    const [linkPopOpen, setLinkPopOpen] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [linkText, setLinkText] = useState('');
    const [linkType, setLinkType] = useState('normal');
    const [linkUrlMode, setLinkUrlMode] = useState('url');
    const savedRangeRef = useRef(null);

    const applyColor = (color) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        if (savedRangeRef.current) {
            const sel = doc.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
        }
        const varSpans = [...doc.querySelectorAll('[data-var="true"]')];
        varSpans.forEach((s) => s.removeAttribute('contenteditable'));
        doc.execCommand('foreColor', false, color);
        varSpans.forEach((s) => { s.contentEditable = 'false'; });
        onChangeRef.current?.(stripVarSpans(doc.body.innerHTML));
        setActiveColor(color);
        setColorPopOpen(false);
    };

    const insertLink = () => {
        const url = linkUrl.trim();
        if (!url) return;
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        doc.body.focus();
        const sel = doc.getSelection();
        sel.removeAllRanges();
        if (savedRangeRef.current) {
            sel.addRange(savedRangeRef.current);
        } else {
            const r = doc.createRange();
            r.selectNodeContents(doc.body);
            r.collapse(false);
            sel.addRange(r);
        }
        const safeUrl = url.replace(/"/g, '&quot;');
        const display = linkText.trim()
            ? linkText.trim().replace(/&/g, '&amp;').replace(/</g, '&lt;')
            : url.replace(/&/g, '&amp;');
        const html = linkType === 'button'
            ? `<a href="${safeUrl}" style="padding:13px 40px;background-color:#18365E;color:#fff;border-radius:8px;text-decoration:none;display:inline-block;font-size: 16px;" target="_blank">${display}</a>`
            : `<a href="${safeUrl}" target="_blank">${display}</a>`;
        doc.execCommand('insertHTML', false, html);
        onChangeRef.current?.(stripVarSpans(doc.body.innerHTML));
        // Register any {{...}} variables used in the URL
        [...url.matchAll(/{{([^}]+)}}/g)].forEach((m) => onVarInsert?.(m[1].trim()));
        setLinkUrl('');
        setLinkText('');
        setLinkPopOpen(false);
        savedRangeRef.current = null;
    };

    const applyFontSize = (size) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        if (savedRangeRef.current) {
            const sel = doc.getSelection();
            sel.removeAllRanges();
            sel.addRange(savedRangeRef.current);
        }
        // Unlock var spans so execCommand includes them in its range
        const varSpans = [...doc.querySelectorAll('[data-var="true"]')];
        varSpans.forEach((s) => s.removeAttribute('contenteditable'));
        doc.execCommand('styleWithCSS', false, false);
        doc.execCommand('fontSize', false, 7);
        varSpans.forEach((s) => { s.contentEditable = 'false'; });
        doc.querySelectorAll('font[size="7"]').forEach((font) => {
            const span = doc.createElement('span');
            span.style.fontSize = `${size}px`;
            span.innerHTML = font.innerHTML;
            font.parentNode.replaceChild(span, font);
        });
        onChangeRef.current?.(stripVarSpans(doc.body.innerHTML));
    };

    const applyLineHeight = (lh) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc || !savedRangeRef.current) return;
        getSelectedBlocks(doc, savedRangeRef.current)
            .forEach((el) => { el.style.lineHeight = lh; });
        onChangeRef.current?.(stripVarSpans(doc.body.innerHTML));
    };

    const insertVar = () => {
        const name = varName.trim();
        if (!name) return;
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        doc.body.focus();
        const sel = doc.getSelection();
        sel.removeAllRanges();
        if (savedRangeRef.current) {
            sel.addRange(savedRangeRef.current);
        } else {
            const r = doc.createRange();
            r.selectNodeContents(doc.body);
            r.collapse(false);
            sel.addRange(r);
        }
        doc.execCommand('insertText', false, `{{${name}}}`);
        onVarInsert?.(name);
        setVarName('');
        setVarPopOpen(false);
        savedRangeRef.current = null;
    };

    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const doc = iframe.contentDocument;

        doc.open();
        doc.write(
            `<html><head><style>${IFRAME_CSS}</style></head><body style="font-family:sans-serif;font-size:14px;margin:8px 12px;outline:none">${value || '<p><br></p>'}</body></html>`
        );
        doc.close();
        doc.designMode = 'on';

        // Protect variables that are already in the template on open
        wrapVarNodes(doc);

        const sync = () => {
            onChangeRef.current?.(stripVarSpans(doc.body.innerHTML));
        };

        let wrapTimer = null;
        const handleInput = () => {
            sync();
            // After the user finishes typing, wrap any newly completed {{...}} patterns
            clearTimeout(wrapTimer);
            wrapTimer = setTimeout(() => wrapVarNodes(doc), 500);
        };

        doc.addEventListener('input', handleInput);
        return () => {
            clearTimeout(wrapTimer);
            doc.removeEventListener('input', handleInput);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const exec = (cmd) => {
        const doc = iframeRef.current?.contentDocument;
        if (!doc) return;
        // Temporarily unlock var spans so the command can include them in its range.
        // Without this, execCommand skips contenteditable=false elements entirely.
        const varSpans = [...doc.querySelectorAll('[data-var="true"]')];
        varSpans.forEach((s) => s.removeAttribute('contenteditable'));
        doc.execCommand(cmd, false, null);
        varSpans.forEach((s) => { s.contentEditable = 'false'; });
    };

    return (
        <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{
                display: 'flex', gap: 2, padding: '5px 8px',
                background: '#fafafa', borderBottom: '1px solid #d9d9d9',
                flexWrap: 'wrap', alignItems: 'center',
            }}>
                {TOOLBAR_GROUPS.map((group, gi) => (
                    <React.Fragment key={gi}>
                        {gi > 0 && (
                            <span style={{
                                width: 1, height: 18, background: '#d9d9d9',
                                margin: '0 4px', display: 'inline-block', flexShrink: 0,
                            }} />
                        )}
                        {group.map(({ cmd, Icon, title }) => (
                            <button
                                key={cmd}
                                type="button"
                                title={title}
                                onMouseDown={(e) => { e.preventDefault(); exec(cmd); }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#e8e8e8'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                style={btnBase}
                            >
                                <Icon size={16} />
                            </button>
                        ))}
                    </React.Fragment>
                ))}
                {/* Line height */}
                <span style={{ width: 1, height: 18, background: '#d9d9d9', margin: '0 4px', display: 'inline-block', flexShrink: 0 }} />
                <select
                    title="Line height"
                    value={lineHeight}
                    onMouseDown={() => {
                        const doc = iframeRef.current?.contentDocument;
                        const sel = doc?.getSelection();
                        savedRangeRef.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                    }}
                    onChange={(e) => {
                        const lh = e.target.value;
                        setLineHeight('');
                        if (lh) applyLineHeight(lh);
                    }}
                    style={{
                        border: '1px solid #d9d9d9',
                        background: '#fff',
                        borderRadius: 4,
                        padding: '2px 4px',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: '#444',
                        height: 26,
                    }}
                >
                    <option value="" disabled>↕ LH</option>
                    {LINE_HEIGHTS.map((v) => (
                        <option key={v} value={v}>{v}</option>
                    ))}
                </select>

                {/* Font size */}
                <span style={{ width: 1, height: 18, background: '#d9d9d9', margin: '0 4px', display: 'inline-block', flexShrink: 0 }} />
                <select
                    title="Font size"
                    value={fontSize}
                    onMouseDown={() => {
                        const doc = iframeRef.current?.contentDocument;
                        const sel = doc?.getSelection();
                        savedRangeRef.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                    }}
                    onChange={(e) => {
                        const sz = e.target.value;
                        setFontSize(sz);
                        if (sz) applyFontSize(Number(sz));
                    }}
                    style={{
                        border: '1px solid #d9d9d9',
                        background: '#fff',
                        borderRadius: 4,
                        padding: '2px 4px',
                        cursor: 'pointer',
                        fontSize: 12,
                        color: '#444',
                        height: 26,
                    }}
                >
                    {FONT_SIZES.map((v) => (
                        <option key={v} value={v}>{v}px</option>
                    ))}
                </select>

                {/* Text color */}
                <span style={{ width: 1, height: 18, background: '#d9d9d9', margin: '0 4px', display: 'inline-block', flexShrink: 0 }} />
                <Popover
                    open={colorPopOpen}
                    onOpenChange={(o) => setColorPopOpen(o)}
                    trigger="click"
                    title="Text color"
                    content={
                        <div style={{ width: 192, display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4 }}>
                            {COLORS.map((c) => (
                                <div
                                    key={c}
                                    title={c}
                                    onClick={() => applyColor(c)}
                                    style={{
                                        width: 20,
                                        height: 20,
                                        background: c,
                                        borderRadius: 3,
                                        cursor: 'pointer',
                                        border: c === activeColor ? '2px solid #1677ff' : '1px solid #d9d9d9',
                                        boxSizing: 'border-box',
                                        flexShrink: 0,
                                    }}
                                />
                            ))}
                        </div>
                    }
                >
                    <button
                        type="button"
                        title="Text color"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            const doc = iframeRef.current?.contentDocument;
                            const sel = doc?.getSelection();
                            savedRangeRef.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#e8e8e8'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        style={btnBase}
                    >
                        <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <MdFormatColorText size={16} />
                            <span style={{ display: 'block', height: 3, width: 14, background: activeColor, borderRadius: 1, marginTop: 1 }} />
                        </div>
                    </button>
                </Popover>

                <span style={{ flex: 1 }} />

                {/* Insert link */}
                <Popover
                    open={linkPopOpen}
                    onOpenChange={(o) => { setLinkPopOpen(o); if (!o) { setLinkUrl(''); setLinkText(''); setLinkUrlMode('url'); } }}
                    trigger="click"
                    title="Insert link"
                    content={
                        <div style={{ width: 290, display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {/* Type toggle */}
                            <div style={{ display: 'flex', gap: 0, border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
                                {['normal', 'button'].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setLinkType(t)}
                                        style={{
                                            flex: 1,
                                            padding: '5px 0',
                                            border: 'none',
                                            cursor: 'pointer',
                                            fontSize: 12,
                                            fontWeight: linkType === t ? 600 : 400,
                                            background: linkType === t ? '#18365E' : '#fff',
                                            color: linkType === t ? '#fff' : '#444',
                                            transition: 'background 0.15s',
                                        }}
                                    >
                                        {t === 'normal' ? 'Normal link' : 'Button link'}
                                    </button>
                                ))}
                            </div>
                            {/* Preview for button style */}
                            {linkType === 'button' && (
                                <div style={{ textAlign: 'center' }}>
                                    <a
                                        href="#preview"
                                        onClick={(e) => e.preventDefault()}
                                        style={{ padding: '8px 24px', backgroundColor: '#18365E', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 13, display: 'inline-block' }}
                                    >
                                        {linkText || 'Link text'}
                                    </a>
                                </div>
                            )}
                            {/* URL / Variable mode toggle */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 11, color: '#888', flexShrink: 0 }}>Link to:</span>
                                <div style={{ display: 'flex', border: '1px solid #d9d9d9', borderRadius: 4, overflow: 'hidden' }}>
                                    {['url', 'variable'].map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            onClick={() => { setLinkUrlMode(m); setLinkUrl(''); }}
                                            style={{
                                                padding: '2px 10px',
                                                border: 'none',
                                                borderRight: m === 'url' ? '1px solid #d9d9d9' : 'none',
                                                cursor: 'pointer',
                                                fontSize: 11,
                                                fontWeight: linkUrlMode === m ? 600 : 400,
                                                background: linkUrlMode === m ? '#1677ff' : '#fff',
                                                color: linkUrlMode === m ? '#fff' : '#555',
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            {m === 'url' ? 'URL' : 'Variable'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {linkUrlMode === 'url' ? (
                                <Input
                                    size="small"
                                    autoFocus
                                    placeholder="https://example.com"
                                    value={linkUrl}
                                    onChange={(e) => setLinkUrl(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') insertLink(); if (e.key === 'Escape') setLinkPopOpen(false); }}
                                />
                            ) : (
                                <Input
                                    size="small"
                                    autoFocus
                                    placeholder="variable name"
                                    addonBefore="{{"
                                    addonAfter="}}"
                                    value={linkUrl.replace(/^\{\{|\}\}$/g, '')}
                                    onChange={(e) => setLinkUrl(e.target.value ? `{{${e.target.value}}}` : '')}
                                    onKeyDown={(e) => { if (e.key === 'Enter') insertLink(); if (e.key === 'Escape') setLinkPopOpen(false); }}
                                />
                            )}
                            <Input
                                size="small"
                                placeholder="Link text (optional)"
                                value={linkText}
                                onChange={(e) => setLinkText(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') insertLink(); if (e.key === 'Escape') setLinkPopOpen(false); }}
                            />
                            <Button size="small" type="primary" onClick={insertLink} disabled={!linkUrl.trim()}>
                                Insert
                            </Button>
                        </div>
                    }
                >
                    <button
                        type="button"
                        title="Insert link"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            const doc = iframeRef.current?.contentDocument;
                            const sel = doc?.getSelection();
                            savedRangeRef.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                            if (sel?.rangeCount) {
                                const selText = sel.getRangeAt(0).toString().trim();
                                if (selText) setLinkText(selText);
                            }
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#e6f0ff'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        style={{
                            ...btnBase,
                            gap: 3,
                            border: '1px dashed #91caff',
                            color: '#0958d9',
                            padding: '2px 7px',
                            fontSize: 12,
                        }}
                    >
                        <MdInsertLink size={14} />
                        Link
                    </button>
                </Popover>

                <Popover
                    open={varPopOpen}
                    onOpenChange={(o) => { setVarPopOpen(o); if (!o) setVarName(''); }}
                    trigger="click"
                    title="Insert variable"
                    content={
                        <div style={{ display: 'flex', gap: 6, width: 230 }}>
                            <Input
                                autoFocus
                                size="small"
                                placeholder="e.g. firstName"
                                value={varName}
                                onChange={(e) => setVarName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') insertVar();
                                    if (e.key === 'Escape') { setVarPopOpen(false); setVarName(''); }
                                }}
                                addonBefore="{{"
                                addonAfter="}}"
                            />
                            <Button size="small" type="primary" onClick={insertVar}>
                                Insert
                            </Button>
                        </div>
                    }
                >
                    <button
                        type="button"
                        title="Insert variable"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            const doc = iframeRef.current?.contentDocument;
                            const sel = doc?.getSelection();
                            savedRangeRef.current = sel?.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7e6'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        style={{
                            ...btnBase,
                            gap: 3,
                            border: '1px dashed #ffc069',
                            color: '#d46b08',
                            padding: '2px 7px',
                            fontSize: 12,
                        }}
                    >
                        <MdAdd size={14} />
                        Variable
                    </button>
                </Popover>
            </div>
            <iframe
                ref={iframeRef}
                title="html-body-editor"
                style={{ width: '100%', height: 420, border: 'none', display: 'block' }}
            />
        </div>
    );
};

const HtmlEditorField = ({ value, onChange, onBodyChange, onVarInsert }) => {
    const handleChange = (html) => {
        onChange?.(html);
        onBodyChange?.(html);
    };
    return <HtmlEditor value={value} onChange={handleChange} onVarInsert={onVarInsert} />;
};

export default HtmlEditorField;
